/**
 * Crew Tab - Client Admin Panel
 *
 * Display and manage crew members in user's organization.
 * Phase 1: Dummy data only ✅
 * Phase 2: Real data with loading/error/empty states ✅
 * Phase 3: Role management and member removal ✅
 */

import React, { useState } from 'react';
import { Users, Loader2, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useClientMembers } from '../../../hooks/client/useClientMembers';
import { usePermissions } from '../../../hooks/usePermissions';

const CrewTab = () => {
  // Phase 2: Real data from Supabase
  const { currentUser, isFleet } = usePermissions();
  const organizationId = currentUser?.organizationId;
  const { members, isLoading, error, refresh, updateMemberRole, removeMember } = useClientMembers(organizationId);

  // Phase 3: State for member management
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Permission check: Fleet role (OWNER/ADMIN) can edit
  const hasEditPermission = isFleet;

  // Handlers
  const handleRoleChange = async (memberId, newRole) => {
    setIsSubmitting(true);
    try {
      const result = await updateMemberRole(memberId, newRole);
      if (result.success) {
        toast.success('Member role updated successfully');
      } else {
        toast.error(result.error || 'Failed to update member role');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (memberId) => {
    setIsSubmitting(true);
    try {
      const result = await removeMember(memberId);
      if (result.success) {
        toast.success('Member removed successfully');
        setDeleteConfirm(null);
      } else {
        toast.error(result.error || 'Failed to remove member');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'OWNER':
        return 'bg-amber-100 text-amber-700';
      case 'ADMIN':
        return 'bg-blue-100 text-blue-700';
      case 'SKIPPER':
        return 'bg-purple-100 text-purple-700';
      case 'CREW':
        return 'bg-slate-100 text-slate-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
        <span className="ml-3 text-slate-600">Loading members...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-red-800 font-medium">Failed to load members</p>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
        <button
          onClick={refresh}
          className="text-red-600 hover:text-red-800 font-medium text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  // Empty state
  if (members.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">
          No team members yet
        </h3>
        <p className="text-slate-600">
          Your organization has no members yet.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">My Crew</h2>
          <p className="text-sm text-slate-500">
            Manage crew members in your organization
          </p>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-medium text-slate-900 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Team Members ({members.length})
          </h3>
        </div>
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Member
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                User ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Joined
              </th>
              {hasEditPermission && (
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {members.map((member) => {
              // Phase 2: Display user_id since profiles table doesn't exist yet
              const displayName = member.user_id || 'Unknown User';
              const initial = displayName.charAt(0).toUpperCase();
              const isCurrentUser = member.user_id === currentUser?.id;

              return (
                <tr key={member.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center">
                        <span className="text-cyan-600 font-medium">
                          {initial}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">
                          {displayName}
                        </div>
                        {isCurrentUser && (
                          <div className="text-xs text-slate-500">(You)</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-slate-600 text-sm">
                    {member.user_id || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {hasEditPermission && !isCurrentUser ? (
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.id, e.target.value)}
                        disabled={isSubmitting}
                        className="px-3 py-1 text-xs font-medium rounded-full border border-slate-300 focus:ring-2 focus:ring-cyan-600 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="OWNER">Owner</option>
                        <option value="ADMIN">Admin</option>
                        <option value="SKIPPER">Skipper</option>
                        <option value="CREW">Crew</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(member.role)}`}>
                        {member.role}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {member.created_at ? new Date(member.created_at).toLocaleDateString('en-GB') : 'N/A'}
                  </td>
                  {hasEditPermission && (
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => setDeleteConfirm(member)}
                        disabled={isCurrentUser || isSubmitting}
                        className="p-2 text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isCurrentUser ? "Cannot remove yourself" : "Remove member"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Remove Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => !isSubmitting && setDeleteConfirm(null)}
            />

            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-red-600 mb-2">Remove Member</h3>
              <p className="text-slate-600 mb-4">
                Are you sure you want to remove this member from your organization?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  They will lose access to all organization data and vessels.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRemove(deleteConfirm.id)}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Removing...' : 'Remove Member'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewTab;
